import { IntercomCustomAttributeKey } from 'constants/IntercomCustomAttributeKey';
import usePlanHandle from 'hooks/billing/usePlanHandle';
import { useShopData } from 'hooks/useShopData';
import { useUserData } from 'hooks/useUserData';
import { User } from 'models/User';
import { useEffect, useRef } from 'react';
import { useIntercom } from 'react-use-intercom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectPackageProviders } from 'redux/slices/package-providers/package-providers.selector';
import {
    selectShopifyAppPlans,
    selectShopifyCurrentSubscription,
} from 'redux/slices/shopify-app-pricing/shopify-app-pricing.selector';
import { selectThirdPartyFulfillmentSettings } from 'redux/slices/third-party-fulfillment-settings/third-party-fulfillment-settings.selector';
import { selectIntercominitialised, setInitialised as setIntercomInitialised } from '../../redux/slices/intercom-slice';
import { useUpdateIntercomFields } from './useUpdateIntercomFields';

const useBootstrapIntercomData = () => {
    const { handleDataToIntercom } = useUpdateIntercomFields();
    const { boot } = useIntercom();
    const ecomSettings = useAppSelector((state) => state.settings?.settings);
    const currentSubscription = useAppSelector(selectShopifyCurrentSubscription);
    const shopifyPlans = useAppSelector(selectShopifyAppPlans);
    const { planHandle, isLoading: planHandleLoading } = usePlanHandle();
    const thirdPartyFulfillmentSettings = useAppSelector(selectThirdPartyFulfillmentSettings);
    const providers = useAppSelector(selectPackageProviders);
    const intercominitialised = useAppSelector(selectIntercominitialised);
    const { data: shopData } = useShopData();
    const initialised = useRef<boolean>(null);
    const dispatch = useAppDispatch();
    const { data: userData } = useUserData();

    const bootIntercom = (userData: User) => {
        boot({
            name: userData.name,
            email: userData.email,
            userId: userData.id.toString(),
            customAttributes: {
                [IntercomCustomAttributeKey.AppName]: import.meta.env.VITE_APP_APP_NAME,
                [IntercomCustomAttributeKey.StoreDomain]: shopify?.config?.shop,
                [IntercomCustomAttributeKey.Uninstalled]: false,
            },
        });
    };

    useEffect(() => {
        if (!userData) {
            return;
        }
        bootIntercom(userData);
        dispatch(setIntercomInitialised(true));
    }, [userData]);

    useEffect(() => {
        if (
            initialised.current ||
            !ecomSettings ||
            !currentSubscription ||
            !shopifyPlans ||
            planHandleLoading ||
            !shopData ||
            !thirdPartyFulfillmentSettings ||
            !providers?.length ||
            !intercominitialised
        )
            return;
        initialised.current = true;
        // The plan's name from the backend's Shopify plans list, found by the subscription's plan handle. A legacy
        // subscription has no plan handle, so it falls back to the subscription's own name.
        const planName =
            shopifyPlans.plans.find((plan) => plan.handle === planHandle)?.name || currentSubscription.name;
        handleDataToIntercom(
            ecomSettings,
            currentSubscription,
            shopData,
            thirdPartyFulfillmentSettings,
            providers,
            planName
        );
    });
};

export default useBootstrapIntercomData;
